// Analytics root state.
//   /analytics                    — project / site visitor dashboard
//   /workspace-analytics          — workspace "What's now" dashboard
//   /workspace-analytics/map      — full-screen world map
//   /workspace-analytics/logs/*   — Users / Sessions / Events / Bugs / Network / Verbose tables
//   /workspace-analytics/logs/sessions/:id — session detail
//
// All keys are top-level (smbls v3.14 schema check accepts root.update on
// declared keys); underscore prefixes are framework-internal flags.

export default {
  // ── /analytics (+ /analytics/map) ──────────────────────────────────
  _analyticsSessions: null,
  _analyticsActiveUsers: null,
  _analytics: null,
  _analyticsExpanded: null,
  _analyticsMapMode: 'sessions',
  _mapHoveredCountry: null,
  _analyticsInterval: '7d',
  _analyticsServerSource: null,
  _analyticsSampleSize: 0,

  // Re-render signal fired by the async world-atlas loader (map +
  // demographics silhouettes). Was written by both consumers but never
  // DECLARED, so smbls dropped the update and the silhouettes stayed
  // blank until an unrelated state tick — declaring it is the fix.
  _worldAtlasTick: 0,

  // ── /workspace-analytics (workspace usage dashboard) ───────────────
  // Analyzed-chain cards, scoped to the shell's own telemetry
  // (projectId 'workspace'): day-by-day team activity, top bug
  // clusters, and the open-tasks count from tickets.columnCounts.
  _wsaWeekly: null,
  _wsaBugs: null,
  _wsaOpenTasks: null,
  // Active-now roster from the analyzed chain (activeUsers scoped to the
  // shell's own telemetry) — the workspace half of the surface division:
  // "who's active" for TEAM members lives here, not on /analytics.
  _wsaActive: null,
  // HEALTH-FALSE-OK-1 / FALSE-ZERO-1 (tickets/opus.md) — load FAILURES for
  // the two feeds whose emptiness is user-facing prose. Each `_wsa*` feed
  // above is null until its fetch resolves, so null alone cannot tell
  // "still loading" from "the request threw" — and every fetch used to
  // `.catch` into a bare console.warn, leaving the key null forever while
  // the card printed "No errors in the last 7 days" over 139,491 real
  // errors. DECLARED here on purpose: an undeclared key has its update
  // dropped (the `_worldAtlasTick` lesson at the top of this file), which
  // would silently restore the false all-clear.
  _wsaBugsError: null,
  _wsaActiveError: null,
  // Workspace-family `totals` (7 days, projectId 'workspace') — the extra
  // KPI tiles (sessions / page views / events / bugs / avg session) on
  // /workspace-analytics. Same positive-read rule as the two feeds above.
  _wsaTotals: null,
  _wsaTotalsError: null,

  // Per-project scope for the /analytics (project) dashboard.
  //   _analyticsParams      — { owner, key } handed in by the studio
  //                           Analytics button; consumed once to seed the
  //                           selector, then cleared.
  //   _analyticsProjectKey  — the project-selector's current choice
  //                           ('' = All projects). null until first set so
  //                           the seed-from-params step can run.
  _analyticsParams: null,
  _analyticsProjectKey: null,

  // ── Overall totals + expanded gatherings (WS-ANALYTICS-FAMILY-SPLIT-UTC-
  // TOTALS-1) — the three NEW /core/analyzed reads (`totals`, `pages`,
  // `referrers`) the projects overview fetches beside the six rollups.
  //   _analyticsTotals       — the `totals` payload for the selected interval
  //                            (sessions / uniqueVisitors / pageViews / bugs /
  //                            avgDurationMs / countries / allTime); null until
  //                            positively read.
  //   _analyticsTotalsError  — the read FAILED (old server: unknown op / 404).
  //                            Same rule as `_wsaBugsError`: only a positively
  //                            read feed may print a zero.
  //   _analyticsPages        — top paths [{ path, views, sessions }] or null
  //                            (pending / unavailable — the card hides).
  //   _analyticsReferrers    — { rows: [{ host, sessions }], direct } or null.
  //   _analyticsRollupTz     — the zone the `now` response's hour-of-day
  //                            buckets are in (its `tz` field; we always ask
  //                            for the viewer's zone). null when the server
  //                            sent no `tz` → the slots use UTC hours. It
  //                            drives bucketing only and is never printed.
  _analyticsTotals: null,
  _analyticsTotalsError: null,
  _analyticsPages: null,
  _analyticsReferrers: null,
  _analyticsRollupTz: null,

  // ── UTM campaign attribution (WS-ANALYTICS-UTM-CAMPAIGNS-UI-1, Nika
  // 2026-09-14: "track analytics based on referral") — the `campaigns`
  // read (`sdk.execute('analyzed', 'campaigns', { filter: { ...scope,
  // since, groupBy }, options: { limit } })`) the projects overview fetches
  // beside pages / referrers.
  //   _analyticsCampaigns        — { groupBy, direct, rows: [{ key, source,
  //                                medium, campaign, sessions, uniqueVisitors,
  //                                pageViews, bugs }] } | null (pending /
  //                                unavailable). Only a positively read feed
  //                                may print a number — same rule as totals.
  //   _analyticsCampaignsError   — string | null: the read FAILED (published
  //                                SDK: unknown op; old server: 404). The
  //                                card says so instead of faking zeros.
  //   _analyticsCampaignsGroupBy — the card's group-by pill: 'campaign' |
  //                                'source' | 'medium' | 'content' | 'term'.
  //                                Joins the dashboard cache key.
  _analyticsCampaigns: null,
  _analyticsCampaignsError: null,
  _analyticsCampaignsGroupBy: 'campaign',

  // ── Last 12 days (Nika 2026-09-14: "one more section that shows last 12
  // days of similar chart") — the `daily` read (`sdk.execute('analyzed',
  // 'daily', { filter: { ...scope, days: 12, tz } })` → { tz, days: [{ date,
  // visitors, sessions, pageViews }] }) beside the other rollups.
  //   _analyticsDaily       — { tz, days } | null (pending / unavailable)
  //   _analyticsDailyError  — string | null (published SDK: unknown op; old
  //                           server: 404 → "Daily history needs the new
  //                           analytics server"). A positively read empty
  //                           response is 12 honest zero bars.
  _analyticsDaily: null,
  _analyticsDailyError: null,

  // ── /analytics/logs (rewritten 2026-05-23; sections split 2026-08-08) ──
  // One state slot per section's row buffer. Each page's onCreate
  // populates its own slot via el.call('loadLogsX'); the table reads
  // it via childrenAs:'state'. Events / Network / Verbose used to share
  // one slot, which rendered the previous section's rows through the
  // next section's projection on every switch — now one slot each.
  logsUsers: [],
  logsSessions: [],
  logsEvents: [],
  logsNetwork: [],
  logsVerbose: [],
  logsBugs: [],

  // Session detail. Inside the home PageSlot the URL never changes, so
  // the drill-through target rides state (`_logsSessionId`) and the
  // fixed-path /analytics/logs/session route reads it; the real
  // /analytics/logs/sessions/:id URL keeps working via the path param.
  _logsSessionId: null,
  logsSessionDetail: null,
  logsSessionEvents: [],

  // Drill-through filters for the sessions section — the map's country
  // click sets `_logsCountry`, a users-row click sets `_logsUser`; the
  // sessions page reads both into its fetch filter and shows clearable
  // chips.
  _logsCountry: null,
  _logsUser: null,
  // UTM drill-through filters for the PROJECTS sessions list (a campaigns
  // card row click sets the one its group-by names; the sessions page
  // forwards all three to `listSessions` and shows a clearable chip each).
  // Exact, lowercase source/medium; `__none__` on source = sessions with no
  // utmSource. Declared here on purpose: an undeclared key's update is
  // dropped (the `_worldAtlasTick` lesson above).
  _logsUtmSource: null,
  _logsUtmMedium: null,
  _logsUtmCampaign: null,
  // Visitor drill-through (sessions + unique visitors, Nika 2026-09-14): a
  // visitors-row click / the session detail's Visitor chip sets the durable
  // first-party `visitorId`; the sessions list forwards it to `listSessions`
  // and shows a clearable "Visitor: 3f2a…" chip.
  _logsVisitor: null,

  // Expand/collapse — one row open at a time across whichever table
  // the user is currently looking at.
  logsExpandedRowId: null,

  // Loading + error are shared across sections (only one section is
  // visible at a time, so a single slot is enough).
  logsLoading: false,
  logsError: null,

  // Pagination — pages start with a smaller fetch (30 rows) so first
  // paint over the wire is fast; the LoadMore button bumps in 30-row
  // increments. Cap is 300 to avoid runaway memory + localStorage
  // payloads.
  logsLimit: 30
}
